import { useState } from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
  Face as FaceRecognitionIcon,
  DirectionsCar as ANPRIcon,
} from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FaceRecognitionTable } from './FaceRecognitionTable.tsx';
import { ANPRTable } from './ANPRTable.tsx';
import { SettingsIconButton } from '../../common/SettingsIconButton.tsx';
// import { PageSettingsDialog } from '../../common/PageSettingsDialog.tsx';
// import { SettingsGroup } from '../../common/SettingsGroup.tsx';
// import { SettingsSwitch, SettingsSelect } from '../../common/SettingsControls.tsx';
import React from 'react';

// Default settings for Configure Use Case (commented out – upcoming feature)
// const CONFIGURE_USE_CASE_SETTINGS = {
//   DISPLAY: {
//     SHOW_IMAGES: true,
//     SHOW_EXPIRATION_WARNINGS: true,
//     COMPACT_MODE: false,
//     DEFAULT_VIEW: 'table',
//   },
//   SORTING: {
//     DEFAULT_SORT_BY: 'name',
//     DEFAULT_SORT_ORDER: 'asc',
//   },
//   FILTERING: {
//     SHOW_EXPIRED_ONLY: false,
//     FILTER_BY_DEPARTMENT: 'all',
//   },
//   FEATURES: {
//     ENABLE_BULK_ACTIONS: true,
//     ENABLE_QUICK_EDIT: true,
//     ENABLE_EXPORT: true,
//   },
// };

export function ConfigureUseCaseTab() {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Settings state from config (commented out – upcoming feature)
  // const [showImages, setShowImages] = useState(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.SHOW_IMAGES);
  // const [showExpirationWarnings, setShowExpirationWarnings] = useState(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.SHOW_EXPIRATION_WARNINGS);
  // const [compactMode, setCompactMode] = useState(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.COMPACT_MODE);
  // const [defaultView, setDefaultView] = useState(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.DEFAULT_VIEW);
  // const [sortBy, setSortBy] = useState(CONFIGURE_USE_CASE_SETTINGS.SORTING.DEFAULT_SORT_BY);
  // const [showExpiredOnly, setShowExpiredOnly] = useState(CONFIGURE_USE_CASE_SETTINGS.FILTERING.SHOW_EXPIRED_ONLY);
  // const [enableBulkActions, setEnableBulkActions] = useState(CONFIGURE_USE_CASE_SETTINGS.FEATURES.ENABLE_BULK_ACTIONS);
  // const [enableQuickEdit, setEnableQuickEdit] = useState(CONFIGURE_USE_CASE_SETTINGS.FEATURES.ENABLE_QUICK_EDIT);
  // const handleResetSettings = () => {
  //   setShowImages(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.SHOW_IMAGES);
  //   setShowExpirationWarnings(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.SHOW_EXPIRATION_WARNINGS);
  //   setCompactMode(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.COMPACT_MODE);
  //   setDefaultView(CONFIGURE_USE_CASE_SETTINGS.DISPLAY.DEFAULT_VIEW);
  //   setSortBy(CONFIGURE_USE_CASE_SETTINGS.SORTING.DEFAULT_SORT_BY);
  //   setShowExpiredOnly(CONFIGURE_USE_CASE_SETTINGS.FILTERING.SHOW_EXPIRED_ONLY);
  //   setEnableBulkActions(CONFIGURE_USE_CASE_SETTINGS.FEATURES.ENABLE_BULK_ACTIONS);
  //   setEnableQuickEdit(CONFIGURE_USE_CASE_SETTINGS.FEATURES.ENABLE_QUICK_EDIT);
  // };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Paper
          elevation={1}
          sx={{
            // Precise calculation breakdown:
            // Header (61px) + Footer (40.8px) + Content py top (6px) + Content py bottom (6px) 
            // + Tabs bar (48px) + Tab border (1px) + TabPanel pt (6px) = 168.8px
            height: 'calc(100vh - 168.8px)',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: "20px",
            overflow: 'hidden', // Card itself has no scroll - content scrolls internally
            position: 'relative',
          }}
        >
          {/* Settings Button - Top Right */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
            }}
          >
            <SettingsIconButton
              onClick={() => setSettingsOpen(true)}
              isOpen={settingsOpen}
              tooltipText="Configure Use Case Settings"
              ariaLabel="Configure settings for Face Recognition and ANPR"
            />
          </Box>

          {/* Card Header with Tabs (Secondary Level) */}
          <Box
            sx={{
              bgcolor: '#F9FAFB',
              borderBottom: '1px solid #E5E7EB',
              px: 1, // 8px horizontal padding
              py: 1,
              // pt: 1,
            }}
          >
            {/* Secondary Tabs - Pill/Contained Style (matching AI Camera screen) */}
            <Tabs
              value={activeSubTab}
              onChange={(_, newValue) => setActiveSubTab(newValue)}
              sx={{
                minHeight: 40,
                '& .MuiTabs-flexContainer': {
                  gap: 1, // Gap between tabs
                },
                '& .MuiTabs-indicator': {
                  display: 'none', // Hide default indicator for pill style
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '13px',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 500,
                  minHeight: 40,
                  borderRadius: '10px',
                  px: 1, // 8px left/right padding
                  py: 1,
                  color: '#4A5565',
                  bgcolor: 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 133, 85, 0.08)',
                    color: '#FF8555',
                  },
                  '&.Mui-selected': {
                    bgcolor: '#FFE0D0',
                    color: '#FF8555',
                    fontWeight: 600,
                  },
                  '& .MuiTab-iconWrapper': {
                    mr: 0.5, // Gap between icon and text
                  },
                },
              }}
            >
              <Tab label="Face Recognition" icon={<FaceRecognitionIcon />} iconPosition="start" />
              <Tab label="ANPR" icon={<ANPRIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Tab Content – flex so FaceRecognitionTable / ANPR footer stays in view */}
          <Box
            sx={{
              height: 'calc(100% - 60px)',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {activeSubTab === 0 && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <FaceRecognitionTable />
              </Box>
            )}
            {activeSubTab === 1 && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <ANPRTable />
              </Box>
            )}
          </Box>
        </Paper>
      </motion.div>

      {/* Settings Dialog – show "Upcoming feature" until full settings are enabled */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Settings</DialogTitle>
        <DialogContent>
          <Typography>Upcoming feature</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {/* Original settings dialog (commented out – upcoming feature)
      <PageSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Configure Use Case Settings"
        onReset={handleResetSettings}
      >
        <SettingsGroup
          title="Display Settings"
          description="Configure how records are displayed"
        >
          <SettingsSelect
            label="Default View"
            value={defaultView}
            onChange={(val) => setDefaultView(val as string)}
            options={[
              { value: 'table', label: 'Table View' },
              { value: 'grid', label: 'Grid View' },
              { value: 'card', label: 'Card View' },
            ]}
            description="Choose the default layout for record display"
          />
          <SettingsSwitch
            label="Show Images"
            checked={showImages}
            onChange={setShowImages}
            description="Display profile images and vehicle photos"
          />
          <SettingsSwitch
            label="Show Expiration Warnings"
            checked={showExpirationWarnings}
            onChange={setShowExpirationWarnings}
            description="Highlight records with expired or expiring permits"
          />
          <SettingsSwitch
            label="Compact Mode"
            checked={compactMode}
            onChange={setCompactMode}
            description="Use compact layout to show more rows"
          />
        </SettingsGroup>
        <SettingsGroup
          title="Sorting & Filtering"
          description="Configure sorting and filtering options"
        >
          <SettingsSelect
            label="Default Sort By"
            value={sortBy}
            onChange={(val) => setSortBy(val as string)}
            options={[
              { value: 'name', label: 'Name' },
              { value: 'id', label: 'ID' },
              { value: 'expirationDate', label: 'Expiration Date' },
              { value: 'department', label: 'Department' },
            ]}
            description="Choose how records are sorted by default"
          />
          <SettingsSwitch
            label="Show Expired Only"
            checked={showExpiredOnly}
            onChange={setShowExpiredOnly}
            description="Filter to show only expired records"
          />
        </SettingsGroup>
        <SettingsGroup
          title="Features"
          description="Enable or disable specific features"
          showDivider={false}
        >
          <SettingsSwitch
            label="Enable Bulk Actions"
            checked={enableBulkActions}
            onChange={setEnableBulkActions}
            description="Allow selecting multiple records for bulk operations"
          />
          <SettingsSwitch
            label="Enable Quick Edit"
            checked={enableQuickEdit}
            onChange={setEnableQuickEdit}
            description="Allow inline editing of record properties"
          />
        </SettingsGroup>
      </PageSettingsDialog>
      */}
    </Box>
  );
}